// src/components/resources/quizzes/components/question-types/VisualQuestion.tsx

'use client'

import { useState } from 'react'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import RiFullscreenLine from '~/icons/remix/generated/RiFullscreenLine'
import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

interface VisualQuestionProps {
  question: QuizQuestion
  selectedAnswer: string | string[]
  onAnswerSelect: (answer: string) => void
  showFeedback?: boolean
  isAnswerCorrect?: boolean
  disabled?: boolean
}

/* eslint-disable @next/next/no-img-element */
export function VisualQuestion({
  question,
  selectedAnswer,
  onAnswerSelect,
  showFeedback,
  isAnswerCorrect,
  disabled
}: VisualQuestionProps): JSX.Element {
  const [expandedImage, setExpandedImage] = useState<string | null>(null)

  const setExpandedImageIfExists = (imageUrl: string | undefined): void => {
    if (imageUrl) {
      setExpandedImage(imageUrl)
    }
  }

  return (
    <div className="space-y-6">
      {/* Image Grid */}
      <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
        {question.options.map((option) => {
          const isSelected = selectedAnswer === option.id
          const isCorrectOption = option.id === question.correctAnswer

          return (
            <div key={option.id} className="group relative">
              {/* Image Container */}
              <div
                className={cn(
                  "relative aspect-[1/1] overflow-hidden rounded-xl ring-2 ring-offset-2 transition-all",
                  isSelected
                    ? "ring-indigo-600 dark:ring-indigo-400"
                    : "ring-transparent",
                  disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                  showFeedback && (isCorrectOption
                    ? "ring-green-500 dark:ring-green-400"
                    : isSelected && "ring-red-500 dark:ring-red-400")
                )}
              >
                {/* Image */}
                <img
                  src={option.imageUrl}
                  alt={option.label}
                  className={cn(
                    "h-full w-full object-cover transition-transform duration-300",
                    !disabled && "group-hover:scale-105"
                  )}
                  onClick={() => {
                    if (!disabled) {
                      onAnswerSelect(option.id)
                    }
                  }}
                />

                {/* Fullscreen Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setExpandedImageIfExists(option.imageUrl)
                  }}
                  className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/70 group-hover:opacity-100"
                >
                  <RiFullscreenLine className="h-4 w-4" />
                </button>

                {/* Feedback Overlay */}
                {showFeedback && (isSelected || (isCorrectOption && isAnswerCorrect)) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={cn(
                      "absolute inset-0 flex items-center justify-center bg-black/30 transition-colors",
                      isAnswerCorrect && isCorrectOption ? "bg-green-500/30 dark:bg-green-500/40" : 
                      isSelected ? "bg-red-500/30 dark:bg-red-500/40" : ""
                    )}
                  >
                    {((isAnswerCorrect && isCorrectOption) || (isSelected && !isAnswerCorrect)) ? (
                      <div className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-full",
                        isAnswerCorrect && isCorrectOption ? "bg-green-500 dark:bg-green-400" : "bg-red-500 dark:bg-red-400"
                      )}>
                        {isAnswerCorrect && isCorrectOption ? (
                          <RiCheckLine className="h-6 w-6 text-white" />
                        ) : (
                          <RiCloseLine className="h-6 w-6 text-white" />
                        )}
                      </div>
                    ) : null}
                  </motion.div>
                )}
              </div>

              {/* Caption */}
              <div className="mt-2 text-center">
                <p className={cn(
                  "text-sm font-medium",
                  isSelected
                    ? "text-indigo-600 dark:text-indigo-400"
                    : "text-gray-700 dark:text-gray-300",
                  showFeedback && isSelected && (isAnswerCorrect
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400")
                )}>
                  {option.label}
                </p>
                {option.description && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {option.description}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Fullscreen Image Modal */}
      {expandedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setExpandedImage(null)}
        >
          <img
            src={expandedImage}
            alt="Expanded view"
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
          />
        </div>
      )}
    </div>
  )
} 